import toAppWebviewUrl from '@/utils/approval/toAppWebviewUrl';
import { getApprovalAppTabName } from '@/utils/approval/approvalAppTab';
import {
  getApprovalDocUrl,
  getApprovalViewUrlByType,
  getIntranetDocUrl,
} from '@/utils/urls';
import { resolveApprovalDoc } from '@/components/Approval/@hooks/useNextApprovalDoc';

/**
 * 결재 문서 URL 을 앱 웹뷰로 전송. 채널(tabName)은 URL 호스트로 정해짐 —
 * 레거시 양식은 레거시 웹뷰(approval), 포탈 이관 양식은 포탈 웹뷰(board).
 *
 * replace: 지금 떠 있는 웹뷰 화면을 새 웹뷰로 교체(RN navigation.replace).
 * "다음 결재 문서 열기"처럼 문서가 바뀌면서 웹뷰 종류도 갈아타야 할 때 사용 —
 * push 로 열면 결재할 때마다 스택이 쌓임.
 *
 * replace 는 rnSupportsModalReplace() 가 참일 때만 보낼 것 — 이 필드를 모르는
 * 구버전 앱은 push 로 처리해 모달이 계속 쌓이므로, 호출부가 구버전용 폴백
 * (같은 웹뷰 안 이동)을 따로 태워야 한다.
 */
/** 앱 번들이 MOVE_TAB/MODAL 의 replace 를 지원하는지 (RN 주입 플래그) */
export const rnSupportsModalReplace = (): boolean =>
  typeof window !== 'undefined' && Boolean(window.supportsRnModalReplace);

const sendApprovalDocToApp = (
  url: string,
  options?: { actionType?: 'MOVE_TAB' | 'MODAL'; replace?: boolean },
) => {
  const { actionType = 'MOVE_TAB', replace = false } = options ?? {};

  window?.ReactNativeWebView?.postMessage(
    JSON.stringify({
      actionType,
      tabName: getApprovalAppTabName(url),
      url: toAppWebviewUrl(url),
      ...(replace && { replace: true }),
    }),
  );
};

/** 판별 응답 대기 중인지 — 앱 웹뷰는 전역에 한 장이라 모듈 단위로 잠근다 */
let isOpeningDoc = false;

/**
 * 지금 누른 탭이 가드에 걸려 무시될지. 호출부가 화면 열기 말고도 부수효과
 * (알림 읽음 처리 등)를 가질 때, 열리지도 않은 문서에 그 효과만 남지 않도록 먼저 묻는다.
 */
export const isApprovalDocOpening = (): boolean => isOpeningDoc;

/**
 * 양식 코드를 모르는 진입(알림, 최근 본 문서)에서 문서 id 만으로 앱 화면을 연다.
 * 단건 조회로 양식을 판별한 뒤 v2 는 포탈 화면, 그 외는 레거시 화면으로 보낸다 —
 * 판별 없이 레거시 URL 을 보내면 v2 양식도 앱에서는 레거시 화면으로 열린다.
 *
 * 포탈 뷰어를 항상 경유시키지 않는 이유: 레거시 양식이 웹뷰 안에서 레거시로
 * 리다이렉트되며 로딩이 두 번 걸린다.
 *
 * 판별 응답을 기다리는 사이의 재탭은 무시한다. 앱 화면은 한 번에 한 장만 열면 되고,
 * 목록 항목마다 가드를 두면 서로 다른 문서를 연달아 눌렀을 때 막지 못해 화면이 쌓인다.
 *
 * fallback: 판별 실패(403·네트워크) 시 어디로 보낼지. 조회가 앱에서도 계속 실패하면
 * 뷰어는 양식을 확정하지 못해 인증을 못 타는 iframe 임베드로 남는다. 그래서 기본은
 * 레거시 직행이고, 뷰어의 재조회에 맡기던 호출부만 'viewer' 를 명시한다.
 */
export const openApprovalDocInApp = async (
  docId: string,
  options?: {
    actionType?: 'MOVE_TAB' | 'MODAL';
    replace?: boolean;
    fallback?: 'legacy' | 'viewer';
  },
) => {
  if (isOpeningDoc) return;
  isOpeningDoc = true;

  try {
    const { fallback = 'legacy', ...sendOptions } = options ?? {};
    // 조회가 터져도 화면은 열려야 함 — 폴백으로 흘려보낸다.
    // 지금은 resolveApprovalDoc 이 내부에서 실패를 흡수해 여기까지 오지 않지만,
    // 호출부가 반환 Promise 를 버리므로 그 계약이 바뀌면 곧장 unhandled rejection 이 된다.
    const { typeCode } = await resolveApprovalDoc(docId).catch(() => ({
      typeCode: undefined,
    }));

    const fallbackUrl =
      fallback === 'viewer'
        ? getApprovalDocUrl(docId)
        : getIntranetDocUrl(docId, false, false);
    const url =
      typeCode === undefined
        ? fallbackUrl
        : getApprovalViewUrlByType(docId, typeCode);

    sendApprovalDocToApp(url, sendOptions);
  } finally {
    isOpeningDoc = false;
  }
};

export default sendApprovalDocToApp;
